/**
 * Os eventos que o WebSocket do portal entrega e a leitura defensiva deles — D3 (passo 19, PR 2).
 *
 * Os formatos são os do PR 1 (`docs/superpowers/plans/2026-10-11-passo-19-pr1-d3-servidor.md`, decisões 3, 4 e 6).
 * O tratador (`aoVivoDoPortal.ts`) nunca confia no payload: cada `ler…` devolve o objeto normalizado ou `null`
 * quando falta o essencial — evento torto é ignorado, não derruba a tela. Os campos de texto que a tela mostra
 * (setor, atendente) nunca saem vazios daqui: "undefined" no aviso é defeito (checklist §9 do desenho).
 */

export const TIPOS_DO_PORTAL = {
  badge: "notification:badge_update",
  nova: "notification:new",
  mensagemNova: "atendimento:mensagem_nova",
  lidas: "atendimento:lidas",
} as const;

export type GravidadeDoAviso = "info" | "warning" | "error" | "critical";

export interface BadgeDoPortal {
  unread_count: number;
  /** Ids lidos em outra aba ou aparelho; `null` no evento do conectar. */
  lidas: string[] | null;
  /** Marcou tudo como lido em outra aba ou aparelho. */
  todas: boolean;
}

export interface NotificacaoNovaDoPortal {
  id: string;
  user_id: string | null;
  title: string;
  body: string;
  severity: GravidadeDoAviso;
  link: string | null;
  categoria: string | null;
  aviso: string | null;
  created_at: string | null;
}

export interface MensagemNovaDoAtendimento {
  user_id: string | null;
  session_id: string;
  /** Nome do setor SEM o prefixo de código (o servidor já tira o `[FIN]`). */
  setor: string;
  /** Primeiro nome do atendente (o nome inteiro nunca sai para o parceiro). */
  author_name: string;
  preview: string;
  message_id: string;
  created_at: string | null;
  aviso: string | null;
}

export interface LidasDoAtendimento {
  user_id: string | null;
  session_id: string;
  /** O total do parceiro DEPOIS da leitura. */
  nao_lidas: number;
}

const GRAVIDADES: ReadonlySet<string> = new Set(["info", "warning", "error", "critical"]);

const ehTexto = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";
const textoOuNulo = (v: unknown): string | null => (ehTexto(v) ? v : null);

function comoRegistro(v: unknown): Record<string, unknown> | null {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

function contagem(v: unknown): number | null {
  return typeof v === "number" && Number.isFinite(v) ? Math.max(0, Math.trunc(v)) : null;
}

export function lerBadge(bruto: unknown): BadgeDoPortal | null {
  const d = comoRegistro(bruto);
  const naoLidas = d ? contagem(d.unread_count) : null;
  if (!d || naoLidas === null) return null;
  return {
    unread_count: naoLidas,
    lidas: Array.isArray(d.lidas) ? d.lidas.filter(ehTexto) : null,
    todas: d.todas === true,
  };
}

export function lerNotificacaoNova(bruto: unknown): NotificacaoNovaDoPortal | null {
  const d = comoRegistro(bruto);
  if (!d || !ehTexto(d.id) || !ehTexto(d.title)) return null;
  return {
    id: d.id,
    user_id: textoOuNulo(d.user_id),
    title: d.title,
    body: typeof d.body === "string" ? d.body : "",
    severity: GRAVIDADES.has(String(d.severity)) ? (d.severity as GravidadeDoAviso) : "info",
    link: textoOuNulo(d.link),
    categoria: textoOuNulo(d.categoria),
    aviso: textoOuNulo(d.aviso),
    created_at: textoOuNulo(d.created_at),
  };
}

export function lerMensagemNova(bruto: unknown): MensagemNovaDoAtendimento | null {
  const d = comoRegistro(bruto);
  if (!d || !ehTexto(d.session_id) || !ehTexto(d.message_id)) return null;
  return {
    user_id: textoOuNulo(d.user_id),
    session_id: d.session_id,
    setor: ehTexto(d.setor) ? d.setor : "Atendimento",
    author_name: ehTexto(d.author_name) ? d.author_name : "Atendente",
    preview: typeof d.preview === "string" ? d.preview : "",
    message_id: d.message_id,
    created_at: textoOuNulo(d.created_at),
    aviso: textoOuNulo(d.aviso),
  };
}

export function lerLidas(bruto: unknown): LidasDoAtendimento | null {
  const d = comoRegistro(bruto);
  const naoLidas = d ? contagem(d.nao_lidas) : null;
  if (!d || !ehTexto(d.session_id) || naoLidas === null) return null;
  return { user_id: textoOuNulo(d.user_id), session_id: d.session_id, nao_lidas: naoLidas };
}
